<template>
  <div class="clinic-lobby">
    <div class="lobby-header">
      <v-icon size="32" color="primary" class="mr-3">mdi-hospital-building</v-icon>
      <div>
        <div class="text-h6">Selecciona una clinica</div>
        <div class="text-caption text-grey">Elige la clinica para ver sus conversaciones</div>
      </div>
    </div>

    <div v-if="loading" class="lobby-loading">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else class="clinic-grid">
      <v-card
        v-for="clinic in clinicsWithCounts"
        :key="clinic.id"
        class="clinic-card"
        variant="outlined"
        hover
        @click="$emit('select-clinic', clinic.id)"
      >
        <v-card-text class="d-flex align-center">
          <v-avatar color="primary" variant="tonal" size="44" class="mr-3">
            <v-icon>mdi-hospital-building</v-icon>
          </v-avatar>

          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-medium">
              {{ clinic.name || clinic.clinic_name || `Clinica ${clinic.id}` }}
            </div>
            <div v-if="clinic.city || clinic.location" class="text-caption text-grey">
              {{ clinic.city || clinic.location }}
            </div>
          </div>

          <div class="d-flex align-center ga-2">
            <v-chip
              v-if="clinic.counts.attention > 0"
              size="small"
              color="error"
              variant="elevated"
              class="font-weight-bold"
            >
              <v-icon size="14" class="mr-1">mdi-alert-circle</v-icon>
              {{ clinic.counts.attention }}
            </v-chip>

            <v-chip
              v-if="clinic.counts.unread > 0"
              size="small"
              color="primary"
              variant="tonal"
            >
              {{ clinic.counts.unread }} sin leer
            </v-chip>

            <v-chip
              v-if="clinic.counts.attention === 0 && clinic.counts.unread === 0"
              size="small"
              color="success"
              variant="tonal"
            >
              Al dia
            </v-chip>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useClinicsStore } from '@/stores/clinics'
import { chatHelpers } from '@/plugins/supabase'
import { useAuthStore } from '@/stores/auth'

defineEmits(['select-clinic'])

const clinicsStore = useClinicsStore()
const authStore = useAuthStore()

const loading = ref(true)
const counts = ref({})
let refreshInterval = null

const availableClinics = computed(() => {
  const userClinics = authStore.assignedClinics
  if (authStore.hasAllClinicsAccess || !userClinics) {
    return clinicsStore.clinics
  }
  const ids = userClinics.map(c => typeof c === 'object' ? String(c.id) : String(c))
  return clinicsStore.clinics.filter(c => ids.includes(String(c.id)))
})

const clinicsWithCounts = computed(() =>
  availableClinics.value.map(clinic => ({
    ...clinic,
    counts: counts.value[String(clinic.id)] || { attention: 0, unread: 0, total: 0 }
  })).sort((a, b) => b.counts.attention - a.counts.attention || b.counts.unread - a.counts.unread)
)

async function loadCounts() {
  try {
    const clinicIds = availableClinics.value.map(c => String(c.id))
    if (clinicIds.length === 0) return
    counts.value = await chatHelpers.getConversationCountsByClinic(clinicIds)
  } catch (error) {
    console.error('[Yapp] Error loading clinic counts:', error)
  }
}

onMounted(async () => {
  if (!clinicsStore.clinics.length) {
    await clinicsStore.fetchClinics()
  }
  await loadCounts()
  loading.value = false
  refreshInterval = setInterval(loadCounts, 30000)
})

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval)
})
</script>

<style scoped>
.clinic-lobby {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  background: #f0f2f5;
  overflow-y: auto;
}

.lobby-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.lobby-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clinic-grid {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clinic-card {
  cursor: pointer;
  transition: all 0.2s;
}

.clinic-card:hover {
  border-color: #075E54;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
